import { createRoute, redirect, useLoaderData, useRouterState } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { shareWishlistConfirmationPageQueryOptions } from "~/app/api/pageData/shareWishlistConfirmationPageQuery";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import { createShareWishlistConfirmationPage } from "~/pages/ShareWishlistConfirmationPage/ShareWishlistConfirmationPage";

export type ShareWishlistConfirmationRouteState = { wishlistName: string };

const WISHLIST_PAGE = "/wishlist";

export function requireShareWishlistConfirmationState(state: unknown): ShareWishlistConfirmationRouteState {
  const transientState = state as Partial<ShareWishlistConfirmationRouteState> | undefined;
  if (!transientState?.wishlistName) {
    throw redirect({ href: WISHLIST_PAGE });
  }
  return { wishlistName: transientState.wishlistName };
}

function ShareWishlistConfirmationRoute() {
  const result = useLoaderData({
    from: "/sendWishlistViaEmail/confirmation",
  }) as PageInitialData<void>;
  const shellProps = useStorefrontShellProps();
  const transientState = useRouterState({
    select: (s) => s.location.state,
  }) as ShareWishlistConfirmationRouteState;

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createShareWishlistConfirmationPage({ wishlistName: transientState.wishlistName })}
    </StorefrontShellFrame>
  );
}

export async function loadShareWishlistConfirmationPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(shareWishlistConfirmationPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const shareWishlistConfirmationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sendWishlistViaEmail/confirmation",
  beforeLoad: ({ location }) => requireShareWishlistConfirmationState(location.state),
  loader: async ({ context }) => loadShareWishlistConfirmationPageData(context),
  component: ShareWishlistConfirmationRoute,
});
